import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Spinner } from 'reactstrap';
import {
    ThemedNav,
    ThemedNavItem,
    ThemedNavLink,
} from '../../components/themed/ThemedComponents';
import { TabContent, TabPane } from 'reactstrap';
import {
    Heading2,
    ThemedButton,
    TextLead,
    BigModal,
} from '@awarego-ui/components';
import { useStores } from '../../hooks/use-stores';
import EmailTabContent from './email-settings/EmailTabContent';
import WelcomeMessageTab from './email-settings/WelcomeMessageTab';
import { useEmailForm } from './email-settings/useEmailForm';
import {
    PREVIEW_TYPE_MAP,
    SMART_VARIABLES,
    TAB_CONFIG,
} from './email-settings/constants';
import {
    sanitizeEmailValues,
    copyToClipboard,
    getActiveTabId,
} from './email-settings/utils';

function EmailSettingsContainer({ companyId }) {
    const { store, companyStore } = useStores();
    const [activeTab, setActiveTab] = useState('1');
    const [previewState, setPreviewState] = useState({
        open: false,
        loading: false,
        html: '',
        error: null,
        subject: '',
        type: null,
    });

    const { localStore, initialValue, handleEnabledChange, defaultValues, handleRevertField, loadingDefaultValues } = useEmailForm(
        store,
        companyStore,
        companyId
    );

    const { savingEmailSettings } = companyStore;
    const { assessmentEnabled, trainingEnabled } = store;

    const tabsChange = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    const handlePreview = async () => {
        const type = PREVIEW_TYPE_MAP[activeTab] || 'training';
        const sanitizedValues = sanitizeEmailValues(localStore._form.values());
        setPreviewState({
            open: true,
            loading: true,
            html: '',
            error: null,
            subject: '',
            type,
        });
        try {
            const result = await companyStore.previewEmailSettings(
                companyId,
                type,
                sanitizedValues
            );
            setPreviewState((prev) => ({
                ...prev,
                loading: false,
                html: result?.html || '',
                subject: result?.subject || '',
            }));
        } catch (err) {
            setPreviewState((prev) => ({
                ...prev,
                loading: false,
                error:
                    companyStore.previewEmailError ||
                    'Unable to load email preview',
            }));
        }
    };

    const closePreview = () => {
        setPreviewState((prev) => ({ ...prev, open: false }));
    };

    const renderPreviewModalContent = () => {
        if (previewState.loading) {
            return (
                <div className="email-preview-modal__spinner">
                    <Spinner />
                </div>
            );
        }

        if (previewState.error) {
            return (
                <div className="email-preview-modal__error">
                    {previewState.error}
                </div>
            );
        }

        return (
            <div
                className="email-preview-modal__html"
                dangerouslySetInnerHTML={{ __html: previewState.html }}
            />
        );
    };

    const handleCopyVariable = (variableName, e) => {
        e.preventDefault();
        e.stopPropagation();
        copyToClipboard(variableName);
    };

    // Calculate form validation
    const formValues = localStore._form.values();
    // Pass raw form values to getDirtyEmailSettings - it will handle normalization internally
    const valuesForDirtyCheck = {
        ...formValues,
        trainingHeaderEnabled: formValues.trainingHeaderEnabled,
        trainingFooterEnabled: formValues.trainingFooterEnabled,
        reminderTrainingHeaderEnabled: formValues.reminderTrainingHeaderEnabled,
        reminderTrainingFooterEnabled: formValues.reminderTrainingFooterEnabled,
        assessmentHeaderEnabled: formValues.assessmentHeaderEnabled,
        assessmentFooterEnabled: formValues.assessmentFooterEnabled,
        reminderAssessmentHeaderEnabled: formValues.reminderAssessmentHeaderEnabled,
        reminderAssessmentFooterEnabled: formValues.reminderAssessmentFooterEnabled,
        welcomeMessageEnabled: formValues.welcomeMessageEnabled,
    };
    const dirtyValues = companyStore.getDirtyEmailSettings(valuesForDirtyCheck);
    const hasDirtyValues = Object.keys(dirtyValues).length > 0;

    const subjectFields = ['training_subject', 'reminder_training_subject'];
    if (assessmentEnabled) {
        subjectFields.push('reminder_assessment_subject', 'assessment_subject');
    }
    const areSubjectFieldsValid = subjectFields.every((fieldName) => {
        const field = localStore._form.$(fieldName);
        if (!field) return true;
        const value = field.value || '';
        return typeof value === 'string' && value.trim().length > 0;
    });

    const getSmartVariablesForTab = (tabId) => {
        const varMap = {
            '1': SMART_VARIABLES.training,
            '2': SMART_VARIABLES.trainingReminder,
            '3': SMART_VARIABLES.assessment,
            '4': SMART_VARIABLES.assessmentReminder,
            '5': SMART_VARIABLES.welcome,
        };
        return varMap[tabId] || [];
    };

    const welcomeTabId = getActiveTabId('welcome', trainingEnabled, assessmentEnabled);

    if (loadingDefaultValues) {
        return (
            <div className="top-guide">
                <div className="block new-settings-block">
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                        <Spinner />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="top-guide">
                <div className="block new-settings-block">
                    <div className="row">
                        <div className="col-md-8 ">
                            <Heading2 displayBlock my={8}>
                                Email settings
                            </Heading2>
                            <p className="text-thin">
                                Set up texts, which should display in training, assessment,
                                reminder and welcome e-mails
                            </p>
                        </div>
                    </div>
                    <ThemedNav dashboard>
                        {trainingEnabled && (
                            <>
                                <ThemedNavItem active={activeTab === '1' ? 'active' : ''}>
                                    <ThemedNavLink onClick={() => tabsChange('1')}>
                                        {TAB_CONFIG.training.label}
                                    </ThemedNavLink>
                                </ThemedNavItem>
                                <ThemedNavItem active={activeTab === '2' ? 'active' : ''}>
                                    <ThemedNavLink onClick={() => tabsChange('2')}>
                                        {TAB_CONFIG.trainingReminder.label}
                                    </ThemedNavLink>
                                </ThemedNavItem>
                            </>
                        )}
                        {assessmentEnabled && (
                            <>
                                <ThemedNavItem
                                    active={activeTab === (trainingEnabled ? '3' : '1') ? 'active' : ''}
                                >
                                    <ThemedNavLink
                                        onClick={() => tabsChange(trainingEnabled ? '3' : '1')}
                                    >
                                        {TAB_CONFIG.assessment.label}
                                    </ThemedNavLink>
                                </ThemedNavItem>
                                <ThemedNavItem
                                    active={activeTab === (trainingEnabled ? '4' : '2') ? 'active' : ''}
                                >
                                    <ThemedNavLink
                                        onClick={() => tabsChange(trainingEnabled ? '4' : '2')}
                                    >
                                        {TAB_CONFIG.assessmentReminder.label}
                                    </ThemedNavLink>
                                </ThemedNavItem>
                            </>
                        )}
                        <ThemedNavItem active={activeTab === welcomeTabId ? 'active' : ''}>
                            <ThemedNavLink onClick={() => tabsChange(welcomeTabId)}>
                                Welcome Message
                            </ThemedNavLink>
                        </ThemedNavItem>
                    </ThemedNav>
                    <TabContent activeTab={activeTab}>
                        {trainingEnabled && (
                            <TabPane tabId="1">
                                <EmailTabContent
                                    form={localStore._form}
                                    initialValue={initialValue}
                                    subjectFieldName={TAB_CONFIG.training.subjectField}
                                    greetingFieldName={TAB_CONFIG.training.greetingField}
                                    headerFieldName={TAB_CONFIG.training.headerField}
                                    footerFieldName={TAB_CONFIG.training.footerField}
                                    headerLabel={TAB_CONFIG.training.headerLabel}
                                    footerLabel={TAB_CONFIG.training.footerLabel}
                                    smartVariables={getSmartVariablesForTab('1')}
                                    onCopyVariable={handleCopyVariable}
                                    onEnabledChange={handleEnabledChange}
                                    defaultValues={defaultValues}
                                    onRevertField={handleRevertField}
                                />
                            </TabPane>
                        )}
                        {trainingEnabled && (
                            <TabPane tabId="2">
                                <EmailTabContent
                                    form={localStore._form}
                                    initialValue={initialValue}
                                    subjectFieldName={TAB_CONFIG.trainingReminder.subjectField}
                                    greetingFieldName={TAB_CONFIG.trainingReminder.greetingField}
                                    headerFieldName={TAB_CONFIG.trainingReminder.headerField}
                                    footerFieldName={TAB_CONFIG.trainingReminder.footerField}
                                    headerLabel={TAB_CONFIG.trainingReminder.headerLabel}
                                    footerLabel={TAB_CONFIG.trainingReminder.footerLabel}
                                    smartVariables={getSmartVariablesForTab('2')}
                                    onCopyVariable={handleCopyVariable}
                                    onEnabledChange={handleEnabledChange}
                                    defaultValues={defaultValues}
                                    onRevertField={handleRevertField}
                                />
                            </TabPane>
                        )}
                        {assessmentEnabled && (
                            <TabPane tabId={trainingEnabled ? '3' : '1'}>
                                <EmailTabContent
                                    form={localStore._form}
                                    initialValue={initialValue}
                                    subjectFieldName={TAB_CONFIG.assessment.subjectField}
                                    greetingFieldName={TAB_CONFIG.assessment.greetingField}
                                    headerFieldName={TAB_CONFIG.assessment.headerField}
                                    footerFieldName={TAB_CONFIG.assessment.footerField}
                                    headerLabel={TAB_CONFIG.assessment.headerLabel}
                                    footerLabel={TAB_CONFIG.assessment.footerLabel}
                                    smartVariables={getSmartVariablesForTab('3')}
                                    onCopyVariable={handleCopyVariable}
                                    onEnabledChange={handleEnabledChange}
                                    defaultValues={defaultValues}
                                    onRevertField={handleRevertField}
                                />
                            </TabPane>
                        )}
                        {assessmentEnabled && (
                            <TabPane tabId={trainingEnabled ? '4' : '2'}>
                                <EmailTabContent
                                    form={localStore._form}
                                    initialValue={initialValue}
                                    subjectFieldName={TAB_CONFIG.assessmentReminder.subjectField}
                                    greetingFieldName={TAB_CONFIG.assessmentReminder.greetingField}
                                    headerFieldName={TAB_CONFIG.assessmentReminder.headerField}
                                    footerFieldName={TAB_CONFIG.assessmentReminder.footerField}
                                    headerLabel={TAB_CONFIG.assessmentReminder.headerLabel}
                                    footerLabel={TAB_CONFIG.assessmentReminder.footerLabel}
                                    smartVariables={getSmartVariablesForTab('4')}
                                    onCopyVariable={handleCopyVariable}
                                    onEnabledChange={handleEnabledChange}
                                    defaultValues={defaultValues}
                                    onRevertField={handleRevertField}
                                />
                            </TabPane>
                        )}
                        <TabPane tabId={welcomeTabId}>
                            <WelcomeMessageTab
                                form={localStore._form}
                                initialValue={initialValue}
                                smartVariables={getSmartVariablesForTab('5')}
                                onCopyVariable={handleCopyVariable}
                                onEnabledChange={handleEnabledChange}
                            />
                        </TabPane>
                    </TabContent>
                    <div className="row">
                        <div
                            className="col-md-8 d-flex align-items-center"
                            style={{ gap: '8px' }}
                        >
                            {savingEmailSettings.includes(companyId) ? (
                                <Spinner size="sm" />
                            ) : (
                                <ThemedButton
                                    mode="secondary"
                                    onClick={localStore._form.onSubmit}
                                    airy
                                    text="Save"
                                    disabled={!hasDirtyValues || !areSubjectFieldsValid}
                                />
                            )}
                            <TextLead
                                themed
                                bold
                                link
                                role="button"
                                tabIndex={0}
                                onClick={handlePreview}
                            >
                                Preview
                            </TextLead>
                        </div>
                    </div>
                </div>
            </div>
            <BigModal
                ariaHideApp={false}
                isOpen={previewState.open}
                onRequestClose={closePreview}
                heading={previewState.subject || 'Email preview'}
                headerStyle={{ flexWrap: 'nowrap', paddingTop: '24px' }}
                modalContent={
                    <div className="email-preview-modal__content">
                        {renderPreviewModalContent()}
                    </div>
                }
                shouldCloseOnOverlayClick={!previewState.loading}
            />
        </>
    );
}

export default observer(EmailSettingsContainer);

